function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { Project, PermissionReducer, RoleReducer } from '@leverege/api-redux';
import { Content, List, Label, Flex, Checkbox, Text } from '@leverege/ui-elements';
import { ValuesCache } from '@leverege/value-cache';
import I18N from '@leverege/i18n';
import { filterAndGroupRoles } from "./Util.js";
import FilterInput from "./FilterInput.js";
import S from './RoleSelector.css';
const {
  t
} = I18N.ns('userManagement.role.RoleSelector');

/* Initialize Variables */
const SIMPLE_FILTER = {
  inCustom: true,
  inProject: false,
  inImagine: false,
  search: null
};

/**
 * Role selector.
 */
class RoleSelector extends React.Component {
  constructor(props) {
    super(props);
    const {
      inCustom = true,
      inProject = true,
      inImagine = false
    } = props;
    this.cache = new ValuesCache();
    this.state = {
      selected: {},
      filter: {
        inCustom,
        inProject,
        inImagine,
        search: null
      }
    };
    this.selector = new List.SingleSelector(this, 'selection', {
      idKey: 'id'
    });
  }
  static getDerivedStateFromProps(props, state) {
    const {
      value
    } = props;
    if (value != state.value) {
      const selected = {};
      if (Array.isArray(value)) {
        value.forEach(r => {
          selected[r.id] = r;
        });
      }
      return {
        selected,
        value
      };
    }
    return null;
  }
  fetchRoles() {
    const {
      fetchRoles
    } = this.props;
    if (typeof fetchRoles === 'function') {
      fetchRoles();
      return;
    }
    const {
      dispatch,
      projectId,
      projectRoles,
      imagineRoles,
      allowImagineRoles = true
    } = this.props;
    if (projectId != null && (projectRoles == null || projectRoles.length === 0)) {
      dispatch(Project(projectId).roles().list({
        includePermissions: false
      }));
    }
    if (allowImagineRoles && (imagineRoles == null || imagineRoles.length === 0)) {
      dispatch(Project('imagine').roles().list({
        includePermissions: false
      }));
    }
  }
  getRoles() {
    const {
      getRoles,
      imagineRoles,
      projectRoles
    } = this.props;
    if (typeof getRoles === 'function') {
      return getRoles();
    }
    return {
      imagineRoles,
      projectRoles
    };
  }
  componentDidMount() {
    this.fetchRoles();
  }
  onFilter = event => {
    this.setState({
      [event.data]: event.value
    });
  };
  onRoleToggle = event => {
    const {
      onChange,
      eventData
    } = this.props;
    const {
      selected
    } = this.state;
    const s = {
      ...selected
    };
    if (event.value === false) {
      delete s[event.data.id];
    } else {
      s[event.data.id] = event.data;
    }
    this.setState({
      selected: s
    });
    onChange({
      value: Object.values(s),
      data: eventData
    });
  };
  render() {
    // Get parameters
    const {
      roleFilter,
      itemRenderer,
      style,
      simple = false,
      variants,
      requireRoles = false
    } = this.props;
    const {
      imagineRoles,
      projectRoles
    } = this.getRoles();
    const {
      selected,
      filter
    } = this.state;
    const roles = this.cache.calc('roles', filterAndGroupRoles, projectRoles, imagineRoles, simple ? SIMPLE_FILTER : filter, roleFilter);
    const itemProps = {
      currentSelections: selected,
      className: S.listItem
    };

    // Return component
    return /*#__PURE__*/React.createElement("div", {
      className: S.gridContainer
    }, /*#__PURE__*/React.createElement(Content, {
      relative: true
    }, /*#__PURE__*/React.createElement(Content.Header, null, /*#__PURE__*/React.createElement(Label, {
      variant: variants?.inputLabel
    }, t('userRoles', {
      requiredRoles: requireRoles ? '*' : ''
    })), !simple && /*#__PURE__*/React.createElement(FilterInput, {
      value: filter,
      eventData: "filter",
      onChange: this.onFilter
    })), /*#__PURE__*/React.createElement(Content.Area, null, /*#__PURE__*/React.createElement(List, {
      onEvent: this.onRoleToggle
    }, /*#__PURE__*/React.createElement(List.Dynamic, {
      items: roles,
      component: itemRenderer || RoleItem,
      itemProps: itemProps
    })))), !simple && /*#__PURE__*/React.createElement("div", {
      className: classnames(S.gridItem, S.right)
    }, /*#__PURE__*/React.createElement(Label, {
      variant: variants?.inputLabel
    }, t('selectedRoles')), /*#__PURE__*/React.createElement(List, {
      onEvent: this.onRoleToggle
    }, /*#__PURE__*/React.createElement(List.Dynamic, {
      items: Object.values(selected),
      component: itemRenderer || RoleItem,
      itemProps: itemProps
    }))));
  }
}

/* Export and Connect Module */
export default connect((state, props) => {
  return {
    imagineRoles: RoleReducer.getRoles(state.roles, 'imagine'),
    imagineStatus: PermissionReducer.getStatus(state.permissions, 'imagine'),
    imagineError: PermissionReducer.getError(state.permissions, 'imagine'),
    projectRoles: RoleReducer.getRoles(state.roles, props.projectId),
    projectStatus: PermissionReducer.getStatus(state.permissions, props.projectId),
    projectError: PermissionReducer.getError(state.permissions, props.projectId)
  };
})(RoleSelector);

/**
 * Role item component factory for dynamic list.
 */
function RoleItem(props) {
  const {
    item,
    onEvent,
    editable,
    currentSelections = {},
    ...rest
  } = props;
  const {
    id,
    displayName,
    name
  } = item;
  return /*#__PURE__*/React.createElement(List.Item, _extends({
    item: item
  }, rest), /*#__PURE__*/React.createElement(Flex, {
    variant: "rowM"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    value: currentSelections[id] != null,
    disabled: editable === false,
    eventData: item,
    onChange: onEvent
  }), /*#__PURE__*/React.createElement(Text, {
    variant: "listItemSubtitle"
  }, displayName || name)));
}